// ═══════════════════════════════════════════════════════════════
// GeoOps zone export — GeoJSON / KML / Shapefile
// Converts an array of GeoOpsZone records into a downloadable file
// in any of the three common GIS interchange formats.
// ═══════════════════════════════════════════════════════════════

import type { GeoOpsZone } from '../store/geoopsStore';

// ── Helpers ─────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function escapeXml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Properties exported alongside each feature — keep field names short
// (≤10 chars) so they survive shapefile DBF attribute name limits.
function zoneProperties(zone: GeoOpsZone) {
  return {
    id:       zone.id,
    name:     (zone.name || '').slice(0, 254),
    type:     zone.zoneType,
    status:   zone.status,
    color:    zone.color,
    notes:    (zone.notes || '').slice(0, 254),
    createdBy: (zone.createdBy || '').slice(0, 254),
    updated:  zone.lastUpdated,
  };
}

function zonesToFeatureCollection(zones: GeoOpsZone[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = zones
    .filter(z => !!z.geometry)
    .map(z => ({
      type: 'Feature',
      geometry: z.geometry as GeoJSON.Geometry,
      properties: zoneProperties(z),
    }));
  return { type: 'FeatureCollection', features };
}

// ── GeoJSON export ──────────────────────────────────────────────

export function exportZonesGeoJSON(zones: GeoOpsZone[], filename = 'geoops-zones.geojson') {
  const fc = zonesToFeatureCollection(zones);
  const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/geo+json' });
  downloadBlob(blob, filename);
}

// ── KML export ──────────────────────────────────────────────────

function coordsToKml(coords: number[][]): string {
  // KML expects lon,lat[,alt] tuples space-separated
  return coords.map(([lng, lat]) => `${lng},${lat},0`).join(' ');
}

function geometryToKml(g: GeoJSON.Geometry): string {
  switch (g.type) {
    case 'Point': {
      const [lng, lat] = g.coordinates;
      return `<Point><coordinates>${lng},${lat},0</coordinates></Point>`;
    }
    case 'LineString':
      return `<LineString><coordinates>${coordsToKml(g.coordinates)}</coordinates></LineString>`;
    case 'Polygon': {
      const [outer, ...holes] = g.coordinates;
      const inner = holes.map(h => `<innerBoundaryIs><LinearRing><coordinates>${coordsToKml(h)}</coordinates></LinearRing></innerBoundaryIs>`).join('');
      return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${coordsToKml(outer)}</coordinates></LinearRing></outerBoundaryIs>${inner}</Polygon>`;
    }
    case 'MultiPolygon':
      return `<MultiGeometry>${g.coordinates.map(rings => geometryToKml({ type: 'Polygon', coordinates: rings }) ).join('')}</MultiGeometry>`;
    case 'MultiLineString':
      return `<MultiGeometry>${g.coordinates.map(line => geometryToKml({ type: 'LineString', coordinates: line })).join('')}</MultiGeometry>`;
    default:
      return '';
  }
}

// ARGB hex (KML uses AABBGGRR) — convert from #RRGGBB
function kmlColor(hex: string, alpha = 0.5): string {
  const h = (hex || '#666666').replace('#', '');
  const r = h.slice(0, 2), g = h.slice(2, 4), b = h.slice(4, 6);
  const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
  return `${a}${b}${g}${r}`.toLowerCase();
}

export function exportZonesKML(zones: GeoOpsZone[], incidentName = 'Incident', filename = 'geoops-zones.kml') {
  const placemarks = zones
    .filter(z => !!z.geometry)
    .map(z => {
      const styleId = `style-${z.id}`;
      const fill = kmlColor(z.color || '#666666', (z.opacity ?? 0.4));
      const line = kmlColor(z.color || '#666666', 1);
      return `
    <Style id="${styleId}">
      <LineStyle><color>${line}</color><width>2</width></LineStyle>
      <PolyStyle><color>${fill}</color></PolyStyle>
    </Style>
    <Placemark>
      <name>${escapeXml(z.name || `Zone ${z.id}`)}</name>
      <description><![CDATA[
        <strong>Type:</strong> ${escapeXml(z.zoneType)}<br/>
        <strong>Status:</strong> ${escapeXml(z.status)}<br/>
        ${z.notes ? `<strong>Notes:</strong> ${escapeXml(z.notes)}<br/>` : ''}
        ${z.createdBy ? `<strong>Created by:</strong> ${escapeXml(z.createdBy)}<br/>` : ''}
      ]]></description>
      <styleUrl>#${styleId}</styleUrl>
      ${geometryToKml(z.geometry as GeoJSON.Geometry)}
    </Placemark>`;
    })
    .join('');

  const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(incidentName)} — GeoOps Zones</name>
    <description>Exported from OpenGov EMC GeoOps</description>
    ${placemarks}
  </Document>
</kml>`;

  const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
  downloadBlob(blob, filename);
}

// ── Shapefile export (zip of .shp/.shx/.dbf/.prj) ───────────────

export async function exportZonesShapefile(zones: GeoOpsZone[], filename = 'geoops-zones.zip') {
  // shp-write only handles one geometry type per output, so split by type.
  const polys = zones.filter(z => z.geometry && (z.geometry.type === 'Polygon' || z.geometry.type === 'MultiPolygon'));
  const lines = zones.filter(z => z.geometry && (z.geometry.type === 'LineString' || z.geometry.type === 'MultiLineString'));
  const points = zones.filter(z => z.geometry && z.geometry.type === 'Point');

  if (polys.length === 0 && lines.length === 0 && points.length === 0) {
    throw new Error('No zones with geometry to export');
  }

  // shp-write@0.4 exposes a `write` function on the default export
  const shpwrite = await import('@mapbox/shp-write');
  // Older versions expose default.zip; newer use shpwrite.zip — handle both
  const zipFn = (shpwrite as { zip?: (gj: GeoJSON.FeatureCollection, opts?: Record<string, unknown>) => Promise<Blob | string> | Blob | string })
    .zip
    || (shpwrite as { default?: { zip?: (gj: GeoJSON.FeatureCollection, opts?: Record<string, unknown>) => Promise<Blob | string> | Blob | string } }).default?.zip;

  if (!zipFn) {
    throw new Error('Shapefile writer not available');
  }

  const fc = zonesToFeatureCollection(zones);
  const result = await zipFn(fc, {
    folder: 'geoops-zones',
    types: {
      polygon: 'zones_polygons',
      polyline: 'zones_lines',
      point: 'zones_points',
    },
    outputType: 'blob',
    compression: 'DEFLATE',
  });

  // Some versions return a base64 string instead of a Blob
  if (typeof result === 'string') {
    // Treat as base64 zip
    const bin = atob(result);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    downloadBlob(new Blob([bytes], { type: 'application/zip' }), filename);
  } else {
    downloadBlob(result as Blob, filename);
  }
}
